import React from "react";
import {
  TutorAnalyticsDashboard,
  type TutorAnalyticsDashboardState,
} from "../../components/student/TutorAnalyticsDashboard";
import { CommunicationCanDoProgressCard } from "../../components/student/CommunicationCanDoProgressCard";
import { TutorLessonProgressPanel } from "../../components/student/TutorLessonProgressPanel";
import { MyVocabularyJourneyPanel } from "../../components/vocabulary/MyVocabularyJourneyPanel";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { CommunicationAssignmentsPanel } from "./CommunicationAssignmentsPanel";
import { StudentProgressAnalyticsPanel } from "../../components/student/StudentProgressAnalyticsPanel";
import { MalayLearningProgressSection } from "../../components/malay-formative/MalayLearningProgressSection";
import { useTutorTenantLabels } from "../../modules/tutor/hooks/useTutorTenantLabels";
import { useLearnerCurriculum } from "../../modules/curriculum/hooks/useLearnerCurriculum";
import { resolveLearnerTrackContext } from "../../modules/student-nav/studentPocNavigation";
import { TamilQuickActions } from "../../modules/student-dashboard/components/TamilQuickActions";
import { useTutorAnalytics } from "../../tutor/hooks/useTutorAnalytics";
import { WelcomeHero } from "../../modules/student-dashboard/components/WelcomeHero";
import { LearningOverview } from "../../modules/student-dashboard/components/LearningOverview";
import { HomeworkSummaryCard } from "../../modules/student-dashboard/components/HomeworkSummaryCard";
import { RecentActivityCard } from "../../modules/student-dashboard/components/RecentActivityCard";
import { AiRecommendationCard } from "../../modules/student-dashboard/components/AiRecommendationCard";
import { AchievementsCard } from "../../modules/student-dashboard/components/AchievementsCard";
import { WeeklyDigestCard } from "../../modules/student-dashboard/components/WeeklyDigestCard";
import { useAssignmentDashboardStats } from "../../modules/student-dashboard/hooks/useAssignmentDashboardStats";
import { useStudentProgressAnalyticsData } from "../../modules/student-dashboard/hooks/useStudentProgressAnalyticsData";
import { useVocabularyJourneyData } from "../../modules/student-dashboard/hooks/useVocabularyJourneyData";
import { useMalayFormativeHome } from "../../modules/student-dashboard/hooks/useMalayFormativeHome";
import { MalayQuickActions } from "../../modules/student-dashboard/components/MalayQuickActions";
import { MalayTodaysLearningCard } from "../../modules/student-dashboard/components/MalayTodaysLearningCard";
import { MalayRecentActivityCard } from "../../modules/student-dashboard/components/MalayRecentActivityCard";
import { IbookDashboardCard } from "../../modules/student-dashboard/components/IbookDashboardCard";

export const StudentDashboard: React.FC = () => {
  const { isSchoolFeatureEnabled, loading: featureAccessLoading } = useFeatureAccess();
  const { labels, loading: tutorLabelsLoading } = useTutorTenantLabels();
  const { curriculum, loading: curriculumLoading } = useLearnerCurriculum();
  const { grade, isRosterLoading } = useStudentMe();
  const tenantReady = !tutorLabelsLoading && !curriculumLoading;
  const learnerTrack = tenantReady
    ? resolveLearnerTrackContext({
        curriculum,
        legacyTenantIsMalay: labels.isMalay,
        curriculumLoading,
      })
    : {
        track: 'neutral' as const,
        isLearnerMalay: false,
        isLearnerTamil: false,
        deferMixedTenantPocNav: true,
      };
  const { track: learnerPocTrack, isLearnerMalay, isLearnerTamil } = learnerTrack;

  const showMalayFormative =
    tenantReady &&
    learnerPocTrack === 'malay' &&
    (featureAccessLoading ||
      isSchoolFeatureEnabled("ai_tutor") ||
      isSchoolFeatureEnabled("narrative_writing"));

  const aiTutorEnabled = featureAccessLoading || isSchoolFeatureEnabled("ai_tutor");
  const vocabularyEnabled = featureAccessLoading || isSchoolFeatureEnabled("vocabulary");
  const learningEnabled = featureAccessLoading || isSchoolFeatureEnabled("learning");
  const communicationTutorEnabled =
    featureAccessLoading || isSchoolFeatureEnabled("communication_tutor");

  const tutorAnalyticsHook = useTutorAnalytics({ initialDays: 30 });
  const tutorAnalyticsState: TutorAnalyticsDashboardState = {
    data: tutorAnalyticsHook.data,
    loading: tutorAnalyticsHook.loading,
    error: tutorAnalyticsHook.error,
    refetch: tutorAnalyticsHook.refetch,
  };

  const assignmentQuery = useAssignmentDashboardStats(
    grade,
    learningEnabled && !featureAccessLoading,
    isRosterLoading,
  );
  const progressAnalytics = useStudentProgressAnalyticsData();
  const vocabJourney = useVocabularyJourneyData();
  const malayFormative = useMalayFormativeHome(showMalayFormative);

  const showTamilRecommendations =
    tenantReady &&
    learnerPocTrack === 'neutral' &&
    !isLearnerMalay &&
    (featureAccessLoading || isSchoolFeatureEnabled("ai_tutor"));
  const showWeeklyDigest = showTamilRecommendations;
  const showAchievements =
    tenantReady &&
    learnerPocTrack === 'neutral' &&
    !isLearnerMalay &&
    (communicationTutorEnabled ||
      featureAccessLoading ||
      isSchoolFeatureEnabled("vocabulary"));
  const showEngagementRow = showTamilRecommendations || showWeeklyDigest || showAchievements;

  const genericTutorSessionLabel = `${labels.moduleTitleAiTutor} session`;

  if (tenantReady && learnerPocTrack === 'tamil') {
    return (
      <>
        <WelcomeHero />
        <TamilQuickActions />
      </>
    );
  }

  if (tenantReady && learnerPocTrack === 'malay') {
    return (
      <>
        <WelcomeHero />
        {showMalayFormative ? (
          <MalayTodaysLearningCard
            formativeCards={malayFormative.cards}
            tutorAnalytics={tutorAnalyticsState.data}
            loading={malayFormative.loading || (aiTutorEnabled && tutorAnalyticsState.loading)}
            enabled={showMalayFormative}
          />
        ) : null}
        <MalayQuickActions />
        {(featureAccessLoading || isSchoolFeatureEnabled('ibook_reader_enabled')) ? (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <IbookDashboardCard />
          </div>
        ) : null}
        {showMalayFormative ? <MalayLearningProgressSection /> : null}
        {showMalayFormative ? (
          <MalayRecentActivityCard
            enabled={showMalayFormative}
            formativeCards={malayFormative.cards}
            formativeLoading={malayFormative.loading}
            formativeError={malayFormative.error}
            onRetryFormative={malayFormative.reload}
            tutorAnalytics={tutorAnalyticsState.data}
            tutorLoading={aiTutorEnabled && tutorAnalyticsState.loading}
            tutorError={tutorAnalyticsState.error}
            tutorEnabled={aiTutorEnabled}
            genericTutorSessionLabel={genericTutorSessionLabel}
            onRetryTutor={tutorAnalyticsState.refetch}
            assignmentStats={assignmentQuery.stats}
            assignmentLoading={learningEnabled && assignmentQuery.loading}
            assignmentError={assignmentQuery.error}
            assignmentEnabled={learningEnabled}
            onRetryAssignment={assignmentQuery.reload}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <WelcomeHero />

      <LearningOverview
        tutorAnalytics={tutorAnalyticsState.data}
        tutorLoading={aiTutorEnabled && tutorAnalyticsState.loading}
        tutorEnabled={aiTutorEnabled}
        tutorModuleTitle={labels.moduleTitleAiTutor}
        practiceStreak={vocabJourney.journey.practice_streak}
        streakLoading={vocabularyEnabled && vocabJourney.loading}
        streakEnabled={vocabularyEnabled && !isLearnerMalay}
        progressAnalytics={progressAnalytics.data}
        progressLoading={false}
        progressEnabled={false}
        assignmentStats={assignmentQuery.stats}
        assignmentLoading={learningEnabled && assignmentQuery.loading}
        assignmentEnabled={learningEnabled}
        assignmentError={assignmentQuery.error}
        onRetryAssignment={assignmentQuery.reload}
      />

      {(featureAccessLoading || isSchoolFeatureEnabled('ibook_reader_enabled')) ? (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <IbookDashboardCard />
        </div>
      ) : null}

      {showEngagementRow ? (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {showTamilRecommendations ? (
            <AiRecommendationCard
              data={progressAnalytics.data}
              canDoLookup={progressAnalytics.canDoLookup}
              loading={progressAnalytics.loading}
              error={progressAnalytics.error}
              enabled={showTamilRecommendations}
              onRetry={progressAnalytics.reload}
            />
          ) : null}
          {showAchievements ? <AchievementsCard /> : null}
          {showWeeklyDigest ? <WeeklyDigestCard /> : null}
        </div>
      ) : null}

      <RecentActivityCard
        tutorAnalytics={tutorAnalyticsState.data}
        tutorLoading={aiTutorEnabled && tutorAnalyticsState.loading}
        tutorError={tutorAnalyticsState.error}
        tutorEnabled={aiTutorEnabled}
        genericTutorSessionLabel={genericTutorSessionLabel}
        onRetryTutor={tutorAnalyticsState.refetch}
        assignmentStats={assignmentQuery.stats}
        assignmentLoading={learningEnabled && assignmentQuery.loading}
        assignmentError={assignmentQuery.error}
        assignmentEnabled={learningEnabled}
        onRetryAssignment={assignmentQuery.reload}
      />

      {tenantReady &&
        learnerPocTrack === 'neutral' &&
        !isLearnerMalay &&
        (featureAccessLoading ||
          isSchoolFeatureEnabled("communication_tutor") ||
          isSchoolFeatureEnabled("ai_tutor")) && <CommunicationCanDoProgressCard />}

      {communicationTutorEnabled && learnerPocTrack === 'neutral' && !isLearnerMalay && (
        <CommunicationAssignmentsPanel variant="dashboard" />
      )}

      {(featureAccessLoading || isSchoolFeatureEnabled("vocabulary")) &&
        learnerPocTrack === 'neutral' &&
        !isLearnerMalay && (
        <div className="mb-6">
          <MyVocabularyJourneyPanel />
        </div>
      )}

      {learnerPocTrack === 'neutral' && !isLearnerMalay && <StudentProgressAnalyticsPanel />}

      <HomeworkSummaryCard
        stats={assignmentQuery.stats}
        loading={learningEnabled && assignmentQuery.loading}
        error={assignmentQuery.error}
        enabled={learningEnabled}
        onRetry={assignmentQuery.reload}
      />

      {aiTutorEnabled && learnerPocTrack === 'neutral' && (
        <TutorAnalyticsDashboard days={30} analytics={tutorAnalyticsState} />
      )}

      {learnerPocTrack === 'neutral' && !isLearnerMalay && <TutorLessonProgressPanel />}
    </>
  );
};
